// src/lib/dealResultFromSnapshot.ts
//
// Objektart-Dispatch "gespeicherter Snapshot -> DealScoreResult + Teaser-Metriken
// (Cashflow/Bruttorendite/Vollvermietung)" -- EINE Stelle statt der bisherigen
// Duplikation. Urspruenglich inline in Check.tsx ("loadingExisting"-Stage, Score aus
// dem gespeicherten Snapshot neu berechnen, NIE dem gespeicherten scorePct vertrauen),
// jetzt auch von /deal/:id (DealDetail.tsx) genutzt -- Runde-1-Schritt 2 des gefuehrten
// Ablaufs nach /check (PHASE-4-BEFUND.md). Reine Extraktion ohne Verhaltensaenderung:
// stellt sicher, dass /check und /deal/:id fuer denselben Deal garantiert dieselbe Zahl
// zeigen, statt ueber zwei getrennte, driftende Kopien dieser Logik.
//
// marktRenditePct bewusst NICHT hier drin -- kommt beim Aufrufer direkt aus
// useStandort()/lageInputFromStandort() (useStandort.ts), unabhaengig von der Objektart.
//
// WICHTIG (Nachtest 13.10.2026, PHASE-4-BEFUND.md): snapIn muss der ueber
// withRawPurchaseCostFields() (purchaseCosts.ts) GEMERGTE Input sein -- resolveXSnapshot()
// (etwSnapshot.ts/mfhSnapshot.ts/...) liefert bewusst NICHT die Kaufnebenkosten-Felder
// (nkGrEStPct/...), die ein eigenes, versioniertes Subsystem sind. Reines resolved.input
// ohne diesen Merge fuehrt bei ETW zu NaN (Darlehen faelschlich als 0 behandelt) und bei
// den anderen 4 Typen zu leise falschen Default- statt Ist-Nebenkosten -- siehe
// withRawPurchaseCostFields()' Dateikommentar fuer Details. Alle Aufrufer (Check.tsx,
// DealDetail.tsx, Portfolio.tsx) muessen den Merge VOR dem Aufruf hier durchfuehren.

import { computeEtwMetrics } from "../core/metrics/etw.js";
import { computeMfhMetrics } from "../core/metrics/mfh.js";
import { computeEtwDealScoreFromSnapshotInput, resolveEtwEigenkapital } from "../core/score/etwDealScore.js";
import { computeMfhDealScoreFromSnapshotInput, computeMfhDealScoreFromMetrics, resolveMfhBewirtschaftung, mfhFlaecheFromRawInput } from "../core/score/mfhDealScore.js";
import { computeEfhDealScoreFromSnapshotInput, computeEfhMetrics, resolveEfhEigenkapital, resolveEfhBewirtschaftung, efhFlaecheFromRawInput } from "../core/score/efhDealScore.js";
import {
  computeGewerbeDealScoreFromSnapshotInput, computeGewerbeDealScoreFromMetrics, computeGewerbeMetrics,
  computeGewerbeWertVsKaufpreis, resolveGewerbeEigenkapital,
  resolveGewerbeBewirtschaftung, gewerbeFlaecheFromZonen, gewerbeLegacyBewirtschaftungY1,
} from "../core/score/gewerbeDealScore.js";
import { computeMixedDealScoreFromSnapshotInput, computeMixedDealScoreFromMetrics, computeMixedMetrics, resolveMixedEigenkapital, resolveMixedBewirtschaftung, mixedLegacyBewirtschaftung } from "../core/score/mixedDealScore.js";
import { resolveImportPcts, pctSum, calcPurchaseCosts } from "./purchaseCosts.js";
import type { DealScoreResult, DealUrteil, LageInput } from "../core/score/dealScore";
import { BEWIRTSCHAFTUNG_PAUSCHALE_PCT } from "../core/score/dealScoreConfig.js";
import type { QuickCheckObjektart } from "./quickCheckDraft";

/** Phase-4 Runde 2b, Baustein 4 (Objektseite erweitern): EINE monatliche Cashflow-Zeile --
 *  Kosten/Zins/Tilgung bereits negativ, sodass die Summe aller Zeilen exakt cashflowPerMonth
 *  ergibt (je Objektart im Dispatcher unten gebaut, aus den vorhandenen *Metrics-Feldern
 *  bzw. bei EFH/Gewerbe/Gemischt teils direkt aus snapIn, weil deren Metrics-Typen den
 *  Monats-/Zins-Tilgung-Split bisher nicht einzeln zurueckgeben).
 *  `caption`: optionale, kleinere Unterzeile (Baustein 5, Druebeck-Pruefung 18.10.2026) --
 *  bisher nur fuer die kombinierte Pauschale-Zeile genutzt (siehe pauschaleBreakdownLine()). */
export type DealCashflowBreakdownLine = { label: string; monthlyAmount: number; caption?: string };

/** Bewirtschaftung Pauschal/Einzeln (Baustein 5, Druebeck-Pruefung 18.10.2026, Dominiks
 *  Vorgabe): im Pauschal-Modus EINE kombinierte Zeile "Bewirtschaftung (Pauschale X %)"
 *  statt "Betriebskosten (nicht umlagefaehig) / Verwaltung 0 / Instandhaltung(sruecklage) 0"
 *  -- die 0-Zeilen waren verwirrend (sahen nach "nichts eingerechnet" aus, obwohl die
 *  Pauschale alles davon abdeckt). `pauschaleAmountMonat` ist die Summe aller Pauschal-
 *  Kosten-Komponenten (bei MFH/EFH/Gewerbe bereits 1 Feld, da deren Pauschal-Zweig
 *  nichtUmlagefaehigEff/verwaltungEff auf 0 setzt und alles in die Capex-aehnliche
 *  Komponente packt -- siehe computeMfhMetrics() etc.). Nur fuer MFH/EFH/Gewerbe/Gemischt
 *  (ETW hat keinen Pauschal/Einzeln-Umschalter). */
function pauschaleBreakdownLine(typ: "mfh" | "efh" | "gewerbe" | "mixeduse", pauschaleAmountMonat: number): DealCashflowBreakdownLine {
  const pct = Math.round(BEWIRTSCHAFTUNG_PAUSCHALE_PCT[typ] * 100);
  return {
    label: `Bewirtschaftung (Pauschale ${pct} %)`,
    monthlyAmount: -pauschaleAmountMonat,
    caption: "inkl. Verwaltung, Instandhaltung, Mietausfall",
  };
}

/** Kennzahlen-Block (Dominiks Vorgabe, Baustein 4, Teil 2 Punkt b): Nettorendite = NOI/
 *  All-in (einheitlich ueber alle 5 Typen, unabhaengig von der je Typ intern abweichenden
 *  noiYield-Basis in den *Metrics-Funktionen -- die bleibt dort fuer ihre eigene
 *  Score-Formel unveraendert, hier nur eine zweite, konsistente Anzeige-Groesse). dscr
 *  kommt aus demselben Score-Pillar-Pfad wie ScoreDisplay/dealFinancingEvaluator.ts, nicht
 *  aus den *Metrics-Funktionen (die haben dscr nicht einheitlich). */
export interface DealKennzahlen {
  nettorenditePct: number;
  dscr: number | null;
  noi: number;
  allIn: number;
  kaufpreisView: number;
}

/** Nur Gewerbe/ETW (Dominiks Vorgabe): Modellwert aus Cap-Rate vs. tatsaechlichem Kaufpreis.
 *  capRateObjektPct/capSpreadPct nur bei Gewerbe gesetzt (ETW hat kein Cap-Spread-Konzept). */
export interface DealWertVsKaufpreis {
  wert: number;
  kaufpreis: number;
  diffPct: number;
  capRateObjektPct?: number;
  capSpreadPct?: number;
}

export interface DealKaufnebenkosten {
  grest: number;
  notar: number;
  grundbuch: number;
  makler: number;
  sonstiges: number;
  summe: number;
}

/** Baustein-4-Nachtest (Dominiks Live-Befund 16.10.2026, Punkt A): EIN Darlehensbetrag
 *  fuer alles (Score, Cashflow, Kennzahlen, Prognose, Bankbericht), exakt der Wert, den
 *  der Score/die Cashflow-Aufschluesselung dieser Objektart schon heute verwenden
 *  (rawMetrics.loan -- je Typ anders definiert, siehe Dateikommentar bei `financing`
 *  unten). Vorher berechnete DealPrognose.tsx sein eigenes, falsches `allIn - eigenkapital`
 *  -- das weicht bei MFH systematisch ab, sobald das eingegebene Eigenkapital die
 *  Nebenkosten nicht deckt (dann deckt ein Teil der Nebenkosten weder Darlehen noch EK,
 *  siehe `gapEur`). */
export interface DealFinancing {
  loan: number;
  eigenkapital: number;
  /** >0 nur bei MFH moeglich (einzige Objektart mit Eigenkapital als direktem Eingabefeld
   *  statt eines LTV-Reglers) -- bei den anderen 4 Typen bestimmt der LTV-Regler das
   *  Darlehen direkt, Eigenkapital ist dort rein das Restglied (allIn - loan), eine Luecke
   *  kann rechnerisch nicht entstehen. */
  gapEur: number;
}

export interface DealResultFromSnapshot {
  score: DealScoreResult;
  cashflowPerMonth: number;
  /** Monatliche Rate (Zins+Tilgung), 0 bei Barkauf -- Runde-1-Schritt 4 (Finanzierungs-
   *  Check/Optimierer, PHASE-4-BEFUND.md), siehe dealFinancingEvaluator.ts. */
  ratePerMonth: number;
  bruttorenditePct: number;
  /** Nur bei vermietet="teilweise" (nicht ETW/EFH): Score, wenn die Soll- statt der Ist-Miete gaelte. */
  vollvermietungScore: number | null;
  vollvermietungUrteil: DealUrteil | null;
  vollvermietungMieteMonat: number | null;
  breakdown: DealCashflowBreakdownLine[];
  kennzahlen: DealKennzahlen;
  wertVsKaufpreis: DealWertVsKaufpreis | null;
  kaufnebenkosten: DealKaufnebenkosten;
  /** Phase-4 Runde 2b, Baustein 4 (Bankbericht aus der Akte): das typ-spezifische
   *  *Metrics-Objekt (MfhMetrics/EfhMetrics/GewerbeMetrics/MixedMetrics/EtwMetrics), das
   *  dieser Dispatcher ohnehin schon berechnet -- bewusst roh durchgereicht (als `unknown`,
   *  vom Aufrufer passend zu `objektart` gecastet), damit buildXBankberichtPayload() exakt
   *  dieselben Zahlen liest statt die Anpassungs-/Zonen-Rechnung ein zweites Mal
   *  nachzubilden (Dominiks Vorgabe: "liest NUR aus result/rawMetrics, keine
   *  Parallel-Rechnung"). */
  rawMetrics: unknown;
  /** Siehe DealFinancing-Dateikommentar: EIN Darlehensbetrag fuer alle Verbraucher dieser
   *  Objektart (Cashflow-Aufschluesselung, Kennzahlen, Prognose, Bankbericht, Formular). */
  financing: DealFinancing;
}

/** Siehe DealKennzahlen-Dateikommentar: dscr IMMER ueber den Score-Pillar-Pfad, nicht aus
 *  den *Metrics-Funktionen (die haben das Feld nicht einheitlich). */
function dscrFromScore(score: DealScoreResult): number | null {
  return score.pillars.find((p) => p.key === "finanzierung")?.components.find((c) => c.key === "dscr")?.rawValue ?? null;
}

/**
 * snapIn/snapPg sind die input/playground-Teile eines resolveXSnapshot()-Ergebnisses
 * (etwSnapshot.ts/mfhSnapshot.ts/.../mixedSnapshot.ts), typisiert als `any` wie am
 * urspruenglichen Ort in Check.tsx -- die 5 Snapshot-Input-Typen sind hier bewusst nicht
 * einzeln importiert, um Schritt 2 klein zu halten (keine Typ-Vereinheitlichung).
 */
export function computeDealResultFromSnapshot(
  objektart: QuickCheckObjektart,
  snapIn: any,
  snapPg: any,
  lage: LageInput
): DealResultFromSnapshot {
  let score: DealScoreResult;
  let cashflowPerMonth: number;
  let ratePerMonth: number;
  let bruttorenditePct: number;
  let vollvermietungScore: number | null = null;
  let vollvermietungUrteil: DealUrteil | null = null;
  let vollvermietungMieteMonat: number | null = null;
  let breakdown: DealCashflowBreakdownLine[];
  let noi: number;
  let allIn: number;
  let kaufpreisView: number;
  let wertVsKaufpreis: DealWertVsKaufpreis | null = null;
  let rawMetrics: unknown;

  if (objektart === "mfh") {
    score = computeMfhDealScoreFromSnapshotInput(snapIn, snapPg, lage, snapIn.vermietet);
    const grossRentYear =
      snapIn.mgmtMode === "einheiten"
        ? (snapIn.units as { areaM2: number; rentPerM2: number }[]).reduce((s, u) => s + u.areaM2 * u.rentPerM2 * 12, 0)
        : snapIn.kaltmieteJahr;
    const mfhFlaeche = mfhFlaecheFromRawInput(snapIn);
    const mfhBewirtschaftung = resolveMfhBewirtschaftung(snapIn, grossRentYear, snapIn.leerstandPct, mfhFlaeche);
    const mfhBase = {
      kaufpreis: snapIn.kaufpreis, priceAdjPct: snapPg.priceAdjPct, applyAdjustments: snapPg.applyAdjustments,
      nkPct: pctSum(resolveImportPcts(snapIn, "mfh")), nkRenovierung: snapIn.nkRenovierung, nkSanierung: snapIn.nkSanierung,
      grossRentYear, rentAdjPct: snapPg.rentAdjPct, leerstandPct: snapIn.leerstandPct,
      nichtUmlagefaehigeKosten: snapIn.nichtUmlagefaehigeKosten, capexRuecklagePctBrutto: snapIn.capexRuecklagePctBrutto,
      bewirtschaftungModus: mfhBewirtschaftung.bewirtschaftungModus, verwaltung: mfhBewirtschaftung.verwaltung,
      instandhaltungJeM2Jahr: mfhBewirtschaftung.instandhaltungJeM2Jahr, flaeche: mfhFlaeche,
      eigenkapital: snapIn.eigenkapital, manualLoan: snapIn.manualLoan, darlehenManual: snapIn.darlehenManual,
      zins: snapIn.zins, tilgung: snapIn.tilgung,
    };
    const metrics = computeMfhMetrics(mfhBase);
    rawMetrics = metrics;
    cashflowPerMonth = metrics.monthlyCF;
    ratePerMonth = metrics.annuitaetMonat;
    // Ist-Miete (effRentYear, nach Leerstand), nicht die Soll-/Brutto-Miete (grossRentYear).
    bruttorenditePct = snapIn.kaufpreis > 0 ? (metrics.effRentYear * 100) / snapIn.kaufpreis : 0;
    noi = metrics.noi;
    allIn = metrics.allIn;
    kaufpreisView = metrics.kaufpreisView;
    breakdown = [
      { label: "Mieteinnahmen", monthlyAmount: metrics.monthlyEffRent },
      ...(mfhBewirtschaftung.bewirtschaftungModus === "pauschal"
        ? [pauschaleBreakdownLine("mfh", metrics.monthlyCapex)]
        : [
            { label: "Betriebskosten (nicht umlagefähig)", monthlyAmount: -metrics.monthlyOpex },
            { label: "Verwaltung", monthlyAmount: -metrics.monthlyVerwaltung },
            { label: "Instandhaltungsrücklage", monthlyAmount: -metrics.monthlyCapex },
          ]),
      { label: "Zinsen", monthlyAmount: -metrics.zinsMonat },
      { label: "Tilgung", monthlyAmount: -metrics.tilgungMonat },
    ];
    if (snapIn.vermietet === "teilweise") {
      const sollBase = { ...mfhBase, leerstandPct: 0 };
      const vollResult = computeMfhDealScoreFromMetrics(sollBase, computeMfhMetrics(sollBase), lage, "vollstaendig");
      vollvermietungScore = vollResult.score;
      vollvermietungUrteil = vollResult.urteil;
      vollvermietungMieteMonat = grossRentYear / 12;
    }
  } else if (objektart === "efh") {
    score = computeEfhDealScoreFromSnapshotInput(snapIn, snapPg, lage, snapIn.vermietet);
    const efhNkPct = pctSum(resolveImportPcts(snapIn, "efh"));
    const efhFlaeche = efhFlaecheFromRawInput(snapIn);
    const efhBewirtschaftung = resolveEfhBewirtschaftung(snapIn, efhFlaeche);
    const metrics = computeEfhMetrics({
      kaufpreis: snapIn.kaufpreis, priceAdjPct: snapPg.priceAdjPct, applyAdjustments: snapPg.applyAdjustments,
      nkPct: efhNkPct, jahreskaltmiete: snapIn.jahreskaltmiete, rentAdjPct: snapPg.rentAdjPct,
      mietausfallPct: snapIn.mietausfallPct, nichtUmlagefaehigJahr: snapIn.nichtUmlagefaehigJahr,
      instandhaltungJahr: snapIn.instandhaltungJahr,
      bewirtschaftungModus: efhBewirtschaftung.bewirtschaftungModus, verwaltung: efhBewirtschaftung.verwaltung,
      instandhaltungJeM2Jahr: efhBewirtschaftung.instandhaltungJeM2Jahr, flaeche: efhFlaeche,
      financingOn: snapIn.financingOn,
      eigenkapital: resolveEfhEigenkapital(snapIn, efhNkPct),
      zinsPct: snapIn.zinsPct, tilgungPct: snapIn.tilgungPct,
    });
    rawMetrics = metrics;
    cashflowPerMonth = metrics.noiJahr / 12 - metrics.annuityYear / 12;
    ratePerMonth = metrics.annuityYear / 12;
    bruttorenditePct = snapIn.kaufpreis > 0 ? (snapIn.jahreskaltmiete * 100) / snapIn.kaufpreis : 0;
    noi = metrics.noiJahr;
    allIn = metrics.allIn;
    kaufpreisView = metrics.kaufpreisView;
    breakdown = [
      { label: "Mieteinnahmen", monthlyAmount: metrics.mieteEffektiv / 12 },
      ...(efhBewirtschaftung.bewirtschaftungModus === "pauschal"
        ? [pauschaleBreakdownLine("efh", (metrics.nichtUmlagefaehigEff + metrics.verwaltungEff + metrics.instandhaltungEff) / 12)]
        : [
            { label: "Nicht umlagefähige Kosten", monthlyAmount: -metrics.nichtUmlagefaehigEff / 12 },
            { label: "Verwaltung", monthlyAmount: -metrics.verwaltungEff / 12 },
            { label: "Instandhaltung", monthlyAmount: -metrics.instandhaltungEff / 12 },
          ]),
      { label: "Zinsen", monthlyAmount: -(metrics.loan * snapIn.zinsPct) / 12 },
      { label: "Tilgung", monthlyAmount: -(metrics.loan * snapIn.tilgungPct) / 12 },
    ];
    // EFH bietet "teilweise" nicht an (einzelne Einheit) -- vollvermietungScore bleibt null.
  } else if (objektart === "gewerbe") {
    score = computeGewerbeDealScoreFromSnapshotInput(snapIn, snapPg, lage, snapIn.vermietet);
    const gewerbeNkPct = pctSum(resolveImportPcts(snapIn, "gewerbe"));
    const gewerbeFlaeche = gewerbeFlaecheFromZonen(snapIn.zonen);
    const gewerbeLegacy = gewerbeLegacyBewirtschaftungY1(snapIn.zonen, snapPg.rentAdjPct, snapIn.opexTotalPctBrutto, snapIn.capexRuecklagePctBrutto);
    const gewerbeBewirtschaftung = resolveGewerbeBewirtschaftung(snapIn, gewerbeLegacy.landlordOpexY1, gewerbeLegacy.capexY1, gewerbeLegacy.totalEff, gewerbeFlaeche);
    const gewerbeBase = {
      kaufpreis: snapIn.kaufpreis, priceAdjPct: snapPg.priceAdjPct, applyAdjustments: snapPg.applyAdjustments,
      nkPct: gewerbeNkPct, zonen: snapIn.zonen, rentAdjPct: snapPg.rentAdjPct,
      opexTotalPctBrutto: snapIn.opexTotalPctBrutto, capexRuecklagePctBrutto: snapIn.capexRuecklagePctBrutto,
      bewirtschaftungModus: gewerbeBewirtschaftung.bewirtschaftungModus, nichtUmlagefaehigeKosten: gewerbeBewirtschaftung.nichtUmlagefaehigeKosten,
      verwaltung: gewerbeBewirtschaftung.verwaltung, instandhaltungJeM2Jahr: gewerbeBewirtschaftung.instandhaltungJeM2Jahr, flaeche: gewerbeFlaeche,
      financingOn: snapIn.financingOn, eigenkapital: resolveGewerbeEigenkapital(snapIn, gewerbeNkPct), zinsPct: snapIn.zinsPct, tilgungPct: snapIn.tilgungPct,
    };
    const metrics = computeGewerbeMetrics(gewerbeBase);
    rawMetrics = metrics;
    cashflowPerMonth = (metrics.noiY1 - metrics.annuityYear) / 12;
    ratePerMonth = metrics.annuityYear / 12;
    // Ist-Miete (effRentYearY1, nach Leerstand je Zone), nicht die Soll-/Brutto-Miete (grossRentYearY1).
    bruttorenditePct = snapIn.kaufpreis > 0 ? (metrics.effRentYearY1 * 100) / snapIn.kaufpreis : 0;
    noi = metrics.noiY1;
    allIn = metrics.allIn;
    kaufpreisView = metrics.kaufpreisView;
    breakdown = [
      { label: "Mieteinnahmen", monthlyAmount: metrics.effRentYearY1 / 12 },
      ...(gewerbeBewirtschaftung.bewirtschaftungModus === "pauschal"
        ? [pauschaleBreakdownLine("gewerbe", (metrics.landlordOpexY1 + metrics.verwaltungEff + metrics.capexY1) / 12)]
        : [
            { label: "Betriebskosten (nicht umlagefähig)", monthlyAmount: -metrics.landlordOpexY1 / 12 },
            { label: "Verwaltung", monthlyAmount: -metrics.verwaltungEff / 12 },
            { label: "Instandhaltungsrücklage", monthlyAmount: -metrics.capexY1 / 12 },
          ]),
      { label: "Zinsen", monthlyAmount: -(metrics.loan * snapIn.zinsPct) / 12 },
      { label: "Tilgung", monthlyAmount: -(metrics.loan * snapIn.tilgungPct) / 12 },
    ];
    // "Wert vs. Kaufpreis" (Dominiks Vorgabe, Baustein 4, Teil 2 Punkt b) -- derselbe
    // Modellwert wie GewerbeCheck.tsx (jetzt geteilt, siehe gewerbeDealScore.ts).
    const gewerbeWert = computeGewerbeWertVsKaufpreis(
      metrics.noiY1, snapIn.zonen as { areaM2: number; leaseTermYears: number }[],
      snapIn.bonitaetTop3, snapIn.indexiert, snapIn.capRateAssumed
    );
    wertVsKaufpreis = {
      wert: gewerbeWert.wert,
      kaufpreis: metrics.kaufpreisView,
      diffPct: metrics.kaufpreisView > 0 ? (gewerbeWert.wert - metrics.kaufpreisView) / metrics.kaufpreisView : 0,
      capRateObjektPct: gewerbeWert.capRateObjekt,
      capSpreadPct: gewerbeWert.capSpreadPct,
    };
    if (snapIn.vermietet === "teilweise") {
      const sollBase = { ...gewerbeBase, zonen: (snapIn.zonen as { vacancyPct: number }[]).map((z: any) => ({ ...z, vacancyPct: 0 })) };
      const sollMetrics = computeGewerbeMetrics(sollBase);
      const vollResult = computeGewerbeDealScoreFromMetrics(sollBase, sollMetrics, lage, "vollstaendig");
      vollvermietungScore = vollResult.score;
      vollvermietungUrteil = vollResult.urteil;
      vollvermietungMieteMonat = sollMetrics.grossRentYearY1 / 12;
    }
  } else if (objektart === "mixeduse") {
    score = computeMixedDealScoreFromSnapshotInput(snapIn, snapPg, lage, snapIn.vermietet);
    const mixedNkPct = pctSum(resolveImportPcts(snapIn, "mixeduse"));
    const mixedLegacy = mixedLegacyBewirtschaftung(snapIn.wFl, snapIn.wRentM2, snapIn.wOpexBrutto, snapIn.gFl, snapIn.gRentM2, snapIn.gOpexBrutto);
    const mixedBewirtschaftung = resolveMixedBewirtschaftung(snapIn, mixedLegacy.wNichtUmlagefaehig, mixedLegacy.gNichtUmlagefaehig);
    const mixedBase = {
      kaufpreis: snapIn.kaufpreis, priceAdjPct: snapPg.priceAdjPct, applyAdjustments: snapPg.applyAdjustments,
      nkPct: mixedNkPct, financingOn: snapIn.financingOn, eigenkapital: resolveMixedEigenkapital(snapIn, mixedNkPct),
      zinsPct: snapIn.zinsPct, tilgungPct: snapIn.tilgungPct,
      wFl: snapIn.wFl, wRentM2: snapIn.wRentM2, wLeer: snapIn.wLeer, wOpexBrutto: snapIn.wOpexBrutto, wRentAdjPct: snapPg.wRentAdjPct, wCap: snapIn.wCap,
      gFl: snapIn.gFl, gRentM2: snapIn.gRentM2, gLeer: snapIn.gLeer, gOpexBrutto: snapIn.gOpexBrutto, gRentAdjPct: snapPg.gRentAdjPct, gCap: snapIn.gCap,
      bewirtschaftungModus: mixedBewirtschaftung.bewirtschaftungModus,
      wNichtUmlagefaehig: mixedBewirtschaftung.wNichtUmlagefaehig, wVerwaltung: mixedBewirtschaftung.wVerwaltung, wInstandhaltungJeM2Jahr: mixedBewirtschaftung.wInstandhaltungJeM2Jahr,
      gNichtUmlagefaehig: mixedBewirtschaftung.gNichtUmlagefaehig, gVerwaltung: mixedBewirtschaftung.gVerwaltung, gInstandhaltungJeM2Jahr: mixedBewirtschaftung.gInstandhaltungJeM2Jahr,
    };
    const metrics = computeMixedMetrics(mixedBase);
    rawMetrics = metrics;
    cashflowPerMonth = (metrics.noiJahr - metrics.annuitaetJahr) / 12;
    ratePerMonth = metrics.annuitaetJahr / 12;
    // Ist-Miete (effRentYear, nach Leerstand je Segment), nicht die Soll-/Brutto-Miete (grossRentYear).
    bruttorenditePct = snapIn.kaufpreis > 0 ? (metrics.effRentYear * 100) / snapIn.kaufpreis : 0;
    noi = metrics.noiJahr;
    allIn = metrics.allIn;
    kaufpreisView = metrics.kaufpreisView;
    // Betriebskosten/Instandhaltung bleiben Wohnen+Gewerbe kombiniert als eine Zeile (kein
    // separates Instandhaltungs-Eingabefeld wie bei MFH/EFH/Gewerbe, siehe mixedSnapshot.ts) --
    // Verwaltung (Baustein 5, nur im Einzeln-Modus > 0) bekommt eine eigene Zeile.
    const mixedVerwaltungGesamt = metrics.wVerwaltungEff + metrics.gVerwaltungEff;
    breakdown = [
      { label: "Mieteinnahmen", monthlyAmount: metrics.effRentYear / 12 },
      ...(mixedBewirtschaftung.bewirtschaftungModus === "pauschal"
        ? [pauschaleBreakdownLine("mixeduse", (metrics.effRentYear - metrics.noiJahr) / 12)]
        : [
            { label: "Betriebskosten", monthlyAmount: -(metrics.effRentYear - metrics.noiJahr - mixedVerwaltungGesamt) / 12 },
            { label: "Verwaltung", monthlyAmount: -mixedVerwaltungGesamt / 12 },
          ]),
      { label: "Zinsen", monthlyAmount: -(metrics.loan * snapIn.zinsPct) / 12 },
      { label: "Tilgung", monthlyAmount: -(metrics.loan * snapIn.tilgungPct) / 12 },
    ];
    if (snapIn.vermietet === "teilweise") {
      const sollBase = { ...mixedBase, wLeer: 0, gLeer: 0 };
      const sollMetrics = computeMixedMetrics(sollBase);
      const vollResult = computeMixedDealScoreFromMetrics(sollBase, sollMetrics, lage, "vollstaendig");
      vollvermietungScore = vollResult.score;
      vollvermietungUrteil = vollResult.urteil;
      vollvermietungMieteMonat = sollMetrics.grossRentYear / 12;
    }
  } else {
    score = computeEtwDealScoreFromSnapshotInput(snapIn, snapPg, lage, snapIn.vermietet);
    // computeEtwMetrics() braucht die 5 NK-Felder einzeln (nicht als kombiniertes nkPct
    // wie bei den anderen 4 Typen) -- explizit ueber resolveImportPcts() aufloesen statt
    // sich auf snapIn.nkGrEStPct etc. direkt zu verlassen (robust auch ohne vorherigen
    // withRawPurchaseCostFields()-Merge bzw. bei alten Snapshots ohne diese Felder).
    const etwPcts = resolveImportPcts(snapIn, "etw");
    const etwNkPct = etwPcts.grest + etwPcts.notar + etwPcts.grundbuch + etwPcts.makler + etwPcts.sonstiges;
    const metrics = computeEtwMetrics({
      ...snapIn, ...snapPg,
      nkGrEStPct: etwPcts.grest, nkNotarPct: etwPcts.notar, nkGrundbuchPct: etwPcts.grundbuch,
      nkMaklerPct: etwPcts.makler, nkSonstPct: etwPcts.sonstiges,
      // Vereinheitlichte Darlehensregel (16.10.2026): ...snapIn oben koennte noch einen
      // nicht aufgeloesten legacyLtvPct-Snapshot enthalten -- eigenkapital hier explizit
      // ueber resolveEtwEigenkapital() ueberschrieben, nicht dem Spread ueberlassen.
      eigenkapital: resolveEtwEigenkapital(snapIn, etwNkPct),
    });
    rawMetrics = metrics;
    cashflowPerMonth = metrics.monthlyCF;
    ratePerMonth = metrics.annuitaetMonat;
    bruttorenditePct = snapIn.kaufpreis > 0 ? (snapIn.flaecheM2 * snapIn.mieteProM2Monat * 12 * 100) / snapIn.kaufpreis : 0;
    noi = metrics.noi;
    allIn = metrics.allIn;
    kaufpreisView = metrics.kaufpreisView;
    breakdown = [
      { label: "Mieteinnahmen", monthlyAmount: metrics.monthlyEffRent },
      { label: "Betriebskosten (nicht umlagefähig)", monthlyAmount: -metrics.monthlyOpex },
      { label: "Instandhaltungsrücklage", monthlyAmount: -metrics.monthlyInstandhaltung },
      { label: "Rücklage Sondereigentum", monthlyAmount: -metrics.monthlySondereigentumRuecklage },
      { label: "Zinsen", monthlyAmount: -metrics.zinsMonat },
      { label: "Tilgung", monthlyAmount: -metrics.tilgungMonat },
    ];
    // "Wert vs. Kaufpreis" (Dominiks Vorgabe) -- ETW hat bereits ein Cap-Rate-Modell
    // (wertNOI, siehe core/metrics/etw.ts), kein Cap-Spread-Konzept wie Gewerbe.
    wertVsKaufpreis = {
      wert: metrics.wertNOI,
      kaufpreis: metrics.kaufpreisView,
      diffPct: metrics.kaufpreisView > 0 ? (metrics.wertNOI - metrics.kaufpreisView) / metrics.kaufpreisView : 0,
    };
    // ETW bietet "teilweise" nicht an (einzelne Einheit) -- vollvermietungScore bleibt null.
  }

  const kennzahlen: DealKennzahlen = {
    nettorenditePct: allIn > 0 ? (noi / allIn) * 100 : 0,
    dscr: dscrFromScore(score),
    noi,
    allIn,
    kaufpreisView,
  };
  const purchaseCosts = calcPurchaseCosts(kaufpreisView, resolveImportPcts(snapIn, objektart));
  const kaufnebenkosten: DealKaufnebenkosten = {
    grest: purchaseCosts.grest,
    notar: purchaseCosts.notar,
    grundbuch: purchaseCosts.grundbuch,
    makler: purchaseCosts.makler,
    sonstiges: purchaseCosts.sonstiges,
    summe: purchaseCosts.sumEur,
  };

  // DealFinancing (siehe Dateikommentar): rawMetrics.loan ist bei allen 5 Typen bereits
  // EXAKT der Wert, den auch der Score/die Cashflow-Aufschluesselung oben verwenden (das
  // *Metrics-Feld heisst bei jedem Typ gleich `loan`, auch wenn die Formel dahinter je Typ
  // frueher unterschiedlich war -- seit 16.10.2026 einheitlich fuer alle 5: EK deckt
  // zuerst die Nebenkosten, Rest reduziert das Darlehen). Eigenkapital/Luecke NICHT ueber
  // currentDealKnobs() (dealFinancingEvaluator.ts) hergeleitet -- die Funktion ist fuer
  // den Finanzierungs-Check/Optimierer gebaut, eine zweite, leicht andere Definition
  // waere hier genau das Problem, das dieser Nachtest beheben soll.
  const financingLoan = (rawMetrics as { loan?: number }).loan ?? 0;
  const financingEigenkapital =
    objektart === "mfh" ? Math.max(0, snapIn.eigenkapital) :
    objektart === "efh" ? Math.max(0, resolveEfhEigenkapital(snapIn, pctSum(resolveImportPcts(snapIn, "efh")))) :
    objektart === "gewerbe" ? Math.max(0, resolveGewerbeEigenkapital(snapIn, pctSum(resolveImportPcts(snapIn, "gewerbe")))) :
    objektart === "mixeduse" ? Math.max(0, resolveMixedEigenkapital(snapIn, pctSum(resolveImportPcts(snapIn, "mixeduse")))) :
    Math.max(0, resolveEtwEigenkapital(snapIn, pctSum(resolveImportPcts(snapIn, "etw"))));
  const financing: DealFinancing = {
    loan: financingLoan,
    eigenkapital: financingEigenkapital,
    // Seit 16.10.2026 (einheitliche Darlehensregel) haben alle 5 *Metrics-Typen ein
    // finanzierungsluecke-Feld -- ?? 0 bleibt nur noch als Typ-Absicherung stehen.
    gapEur: (rawMetrics as { finanzierungsluecke?: number }).finanzierungsluecke ?? 0,
  };

  return {
    score, cashflowPerMonth, ratePerMonth, bruttorenditePct, vollvermietungScore, vollvermietungUrteil,
    vollvermietungMieteMonat, breakdown, kennzahlen, wertVsKaufpreis, kaufnebenkosten, rawMetrics, financing,
  };
}

/**
 * Baut aus einem gespeicherten Snapshot eine (kaufpreis) => monatlicher-Cashflow
 * Closure -- fuer den Break-even-Loeser (dealScoreBreakeven.ts, Schritt 1), hier als
 * Konsument fuer Schritt 3 ("/deal/:id"-Break-even-Anzeige, PHASE-4-BEFUND.md). Alle
 * anderen Werte bleiben auf dem gespeicherten Stand fixiert -- nur der Kaufpreis
 * variiert, exakt wie bei den 4 bisherigen Analyzer-eigenen
 * breakEvenPriceForCashflowZero()-Implementierungen (z. B. Eigentumswohnung.tsx).
 */
export function buildCashflowEvaluator(
  objektart: QuickCheckObjektart,
  snapIn: any,
  snapPg: any
): (kaufpreis: number) => number {
  if (objektart === "mfh") {
    const grossRentYear =
      snapIn.mgmtMode === "einheiten"
        ? (snapIn.units as { areaM2: number; rentPerM2: number }[]).reduce((s, u) => s + u.areaM2 * u.rentPerM2 * 12, 0)
        : snapIn.kaltmieteJahr;
    const nkPct = pctSum(resolveImportPcts(snapIn, "mfh"));
    const mfhFlaeche = mfhFlaecheFromRawInput(snapIn);
    const mfhBewirtschaftung = resolveMfhBewirtschaftung(snapIn, grossRentYear, snapIn.leerstandPct, mfhFlaeche);
    return (kaufpreis) =>
      computeMfhMetrics({
        kaufpreis, priceAdjPct: snapPg.priceAdjPct, applyAdjustments: snapPg.applyAdjustments,
        nkPct, nkRenovierung: snapIn.nkRenovierung, nkSanierung: snapIn.nkSanierung,
        grossRentYear, rentAdjPct: snapPg.rentAdjPct, leerstandPct: snapIn.leerstandPct,
        nichtUmlagefaehigeKosten: snapIn.nichtUmlagefaehigeKosten, capexRuecklagePctBrutto: snapIn.capexRuecklagePctBrutto,
        bewirtschaftungModus: mfhBewirtschaftung.bewirtschaftungModus, verwaltung: mfhBewirtschaftung.verwaltung,
        instandhaltungJeM2Jahr: mfhBewirtschaftung.instandhaltungJeM2Jahr, flaeche: mfhFlaeche,
        eigenkapital: snapIn.eigenkapital, manualLoan: snapIn.manualLoan, darlehenManual: snapIn.darlehenManual,
        zins: snapIn.zins, tilgung: snapIn.tilgung,
      }).monthlyCF;
  }
  if (objektart === "efh") {
    const nkPct = pctSum(resolveImportPcts(snapIn, "efh"));
    const eigenkapital = resolveEfhEigenkapital(snapIn, nkPct);
    const efhFlaeche = efhFlaecheFromRawInput(snapIn);
    const efhBewirtschaftung = resolveEfhBewirtschaftung(snapIn, efhFlaeche);
    return (kaufpreis) => {
      const m = computeEfhMetrics({
        kaufpreis, priceAdjPct: snapPg.priceAdjPct, applyAdjustments: snapPg.applyAdjustments,
        nkPct, jahreskaltmiete: snapIn.jahreskaltmiete, rentAdjPct: snapPg.rentAdjPct,
        mietausfallPct: snapIn.mietausfallPct, nichtUmlagefaehigJahr: snapIn.nichtUmlagefaehigJahr,
        instandhaltungJahr: snapIn.instandhaltungJahr,
        bewirtschaftungModus: efhBewirtschaftung.bewirtschaftungModus, verwaltung: efhBewirtschaftung.verwaltung,
        instandhaltungJeM2Jahr: efhBewirtschaftung.instandhaltungJeM2Jahr, flaeche: efhFlaeche,
        financingOn: snapIn.financingOn, eigenkapital,
        zinsPct: snapIn.zinsPct, tilgungPct: snapIn.tilgungPct,
      });
      return m.noiJahr / 12 - m.annuityYear / 12;
    };
  }
  if (objektart === "gewerbe") {
    const nkPct = pctSum(resolveImportPcts(snapIn, "gewerbe"));
    const eigenkapital = resolveGewerbeEigenkapital(snapIn, nkPct);
    const gewerbeFlaeche = gewerbeFlaecheFromZonen(snapIn.zonen);
    const gewerbeLegacy = gewerbeLegacyBewirtschaftungY1(snapIn.zonen, snapPg.rentAdjPct, snapIn.opexTotalPctBrutto, snapIn.capexRuecklagePctBrutto);
    const gewerbeBewirtschaftung = resolveGewerbeBewirtschaftung(snapIn, gewerbeLegacy.landlordOpexY1, gewerbeLegacy.capexY1, gewerbeLegacy.totalEff, gewerbeFlaeche);
    return (kaufpreis) => {
      const m = computeGewerbeMetrics({
        kaufpreis, priceAdjPct: snapPg.priceAdjPct, applyAdjustments: snapPg.applyAdjustments,
        nkPct, zonen: snapIn.zonen, rentAdjPct: snapPg.rentAdjPct,
        opexTotalPctBrutto: snapIn.opexTotalPctBrutto, capexRuecklagePctBrutto: snapIn.capexRuecklagePctBrutto,
        bewirtschaftungModus: gewerbeBewirtschaftung.bewirtschaftungModus, nichtUmlagefaehigeKosten: gewerbeBewirtschaftung.nichtUmlagefaehigeKosten,
        verwaltung: gewerbeBewirtschaftung.verwaltung, instandhaltungJeM2Jahr: gewerbeBewirtschaftung.instandhaltungJeM2Jahr, flaeche: gewerbeFlaeche,
        financingOn: snapIn.financingOn, eigenkapital, zinsPct: snapIn.zinsPct, tilgungPct: snapIn.tilgungPct,
      });
      return (m.noiY1 - m.annuityYear) / 12;
    };
  }
  if (objektart === "mixeduse") {
    const nkPct = pctSum(resolveImportPcts(snapIn, "mixeduse"));
    const eigenkapital = resolveMixedEigenkapital(snapIn, nkPct);
    const mixedLegacy = mixedLegacyBewirtschaftung(snapIn.wFl, snapIn.wRentM2, snapIn.wOpexBrutto, snapIn.gFl, snapIn.gRentM2, snapIn.gOpexBrutto);
    const mixedBewirtschaftung = resolveMixedBewirtschaftung(snapIn, mixedLegacy.wNichtUmlagefaehig, mixedLegacy.gNichtUmlagefaehig);
    return (kaufpreis) => {
      const m = computeMixedMetrics({
        kaufpreis, priceAdjPct: snapPg.priceAdjPct, applyAdjustments: snapPg.applyAdjustments,
        nkPct, financingOn: snapIn.financingOn, eigenkapital,
        zinsPct: snapIn.zinsPct, tilgungPct: snapIn.tilgungPct,
        wFl: snapIn.wFl, wRentM2: snapIn.wRentM2, wLeer: snapIn.wLeer, wOpexBrutto: snapIn.wOpexBrutto, wRentAdjPct: snapPg.wRentAdjPct, wCap: snapIn.wCap,
        gFl: snapIn.gFl, gRentM2: snapIn.gRentM2, gLeer: snapIn.gLeer, gOpexBrutto: snapIn.gOpexBrutto, gRentAdjPct: snapPg.gRentAdjPct, gCap: snapIn.gCap,
        bewirtschaftungModus: mixedBewirtschaftung.bewirtschaftungModus,
        wNichtUmlagefaehig: mixedBewirtschaftung.wNichtUmlagefaehig, wVerwaltung: mixedBewirtschaftung.wVerwaltung, wInstandhaltungJeM2Jahr: mixedBewirtschaftung.wInstandhaltungJeM2Jahr,
        gNichtUmlagefaehig: mixedBewirtschaftung.gNichtUmlagefaehig, gVerwaltung: mixedBewirtschaftung.gVerwaltung, gInstandhaltungJeM2Jahr: mixedBewirtschaftung.gInstandhaltungJeM2Jahr,
      });
      return (m.noiJahr - m.annuitaetJahr) / 12;
    };
  }
  // etw -- siehe computeDealResultFromSnapshot() oben zur expliziten NK-Aufloesung
  const etwPcts = resolveImportPcts(snapIn, "etw");
  const etwNkPctEval = etwPcts.grest + etwPcts.notar + etwPcts.grundbuch + etwPcts.makler + etwPcts.sonstiges;
  const etwEigenkapitalEval = resolveEtwEigenkapital(snapIn, etwNkPctEval);
  return (kaufpreis) =>
    computeEtwMetrics({
      ...snapIn, ...snapPg, kaufpreis,
      nkGrEStPct: etwPcts.grest, nkNotarPct: etwPcts.notar, nkGrundbuchPct: etwPcts.grundbuch,
      nkMaklerPct: etwPcts.makler, nkSonstPct: etwPcts.sonstiges,
      eigenkapital: etwEigenkapitalEval,
    }).monthlyCF;
}
